import React, { useState, useEffect } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons'
import { toast } from 'react-toastify'
import {
  useTransactionLists,
  useCreateTransactionList,
  useAddToTransactionList,
  useRemoveFromTransactionList
} from 'api/hooks/use-transaction-lists'

const AddToListDropdown = ({ transaction, showLabel = false }) => {
  const [showNewListForm, setShowNewListForm] = useState(false)
  const [newListName, setNewListName] = useState('')
  const [memberListIds, setMemberListIds] = useState(
    () => new Set(transaction?.transaction_list_ids ?? [])
  )

  const transactionId = transaction?.id
  const sectionId = transaction?.section_id

  // Keep local membership state in sync when the underlying transaction data
  // refreshes (e.g. after react-query cache invalidation).
  useEffect(() => {
    setMemberListIds(new Set(transaction?.transaction_list_ids ?? []))
  }, [transaction?.transaction_list_ids])

  const { data: allLists = [], isLoading: listsLoading } = useTransactionLists()

  const { mutate: addToList, isLoading: addingToList } = useAddToTransactionList()
  const { mutate: removeFromList, isLoading: removingFromList } = useRemoveFromTransactionList()
  const { mutate: createList, isLoading: creatingList } = useCreateTransactionList()

  const filteredLists = sectionId
    ? allLists.filter((list) => list.section_id === sectionId)
    : allLists

  const handleAddToList = (listId, listName) => {
    // Optimistically update local state
    setMemberListIds((prev) => new Set([...prev, listId]))
    addToList(
      { listId, transactionId },
      {
        onSuccess: () => {
          toast.success(`Added to list "${listName}"`, { autoClose: 3000 })
        },
        onError: () => {
          // Roll back optimistic update on failure
          setMemberListIds((prev) => {
            const next = new Set(prev)
            next.delete(listId)
            return next
          })
        }
      }
    )
  }

  const handleRemoveFromList = (listId, listName) => {
    // Optimistically update local state
    setMemberListIds((prev) => {
      const next = new Set(prev)
      next.delete(listId)
      return next
    })
    removeFromList(
      { listId, transactionId },
      {
        onSuccess: () => {
          toast.success(`Removed from list "${listName}"`, { autoClose: 3000 })
        },
        onError: () => {
          // Roll back optimistic update on failure
          setMemberListIds((prev) => new Set([...prev, listId]))
        }
      }
    )
  }

  const handleCreateAndAdd = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!newListName.trim()) return

    const nameToCreate = newListName.trim()
    createList(
      { name: nameToCreate, section_id: sectionId },
      {
        onSuccess: (newList) => {
          if (newList?.id) {
            setMemberListIds((prev) => new Set([...prev, newList.id]))
            addToList(
              { listId: newList.id, transactionId },
              {
                onSuccess: () => {
                  toast.success(`Created list "${nameToCreate}" and added transaction`, { autoClose: 3000 })
                },
                onError: () => {
                  setMemberListIds((prev) => {
                    const next = new Set(prev)
                    next.delete(newList.id)
                    return next
                  })
                  toast.error(`Failed to add transaction to list "${nameToCreate}"`)
                }
              }
            )
          }
          setNewListName('')
          setShowNewListForm(false)
        },
        onError: () => {
          toast.error(`Failed to create list "${nameToCreate}"`)
        }
      }
    )
  }

  const isMutating = addingToList || removingFromList || creatingList

  return (
    <Dropdown autoClose="outside" onClick={(e) => e.stopPropagation()}>
      <Dropdown.Toggle
        variant={showLabel ? 'light' : 'link'}
        size="sm"
        id={`add-to-list-${transactionId}`}
        className={showLabel ? '' : 'p-0 text-muted'}
      >
        <FontAwesomeIcon icon={faList} title="Add to list" className={transaction?.transaction_list_ids?.length > 0 ? 'text-primary' : 'text-default'} />
        {showLabel && <span className="ms-2">Add to list</span>}
      </Dropdown.Toggle>

      <Dropdown.Menu style={{ minWidth: '220px' }}>
        {listsLoading && (
          <Dropdown.Item disabled>
            <Spinner animation="border" size="sm" className="me-2" />
            Loading lists...
          </Dropdown.Item>
        )}

        {!listsLoading && filteredLists.length === 0 && !showNewListForm && (
          <Dropdown.Item disabled className="text-muted small">
            No lists yet
          </Dropdown.Item>
        )}

        {!listsLoading &&
          Object.entries(
            filteredLists.reduce((groups, list) => {
              const name = list.section?.name || 'Other'
              if (!groups[name]) groups[name] = []
              groups[name].push(list)
              return groups
            }, {})
          ).map(([sectionName, sectionLists]) => (
            <React.Fragment key={sectionName}>
              <Dropdown.Header>{sectionName}</Dropdown.Header>
              {sectionLists.map((list) => {
                const alreadyAdded = memberListIds.has(list.id)
                return (
                  <Dropdown.Item
                    key={list.id}
                    onClick={() => alreadyAdded ? handleRemoveFromList(list.id, list.name) : handleAddToList(list.id, list.name)}
                    disabled={isMutating}
                    className="d-flex align-items-center justify-content-between"
                  >
                    <span className="text-truncate" style={{ maxWidth: '160px' }}>
                      {list.name}
                    </span>
                    {alreadyAdded && (
                      <FontAwesomeIcon icon={faCheck} className="text-success ms-2 flex-shrink-0" />
                    )}
                  </Dropdown.Item>
                )
              })}
            </React.Fragment>
          ))}

        <Dropdown.Divider />

        {showNewListForm ? (
          <div className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
            <Form onSubmit={handleCreateAndAdd}>
              <Form.Control
                size="sm"
                type="text"
                placeholder="List name"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                autoFocus
                className="mb-2"
              />
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  type="submit"
                  disabled={creatingList || !newListName.trim()}
                >
                  {creatingList ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Create & add'
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => {
                    setShowNewListForm(false)
                    setNewListName('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        ) : (
          <Dropdown.Item
            onClick={() => setShowNewListForm(true)}
            className="text-primary"
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Create new list
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default AddToListDropdown
